import { CircuitAssigned } from "../../pages/layouts/plan/properties/controller/ControllerPropertiesContext";
import { ControllerType } from "../../store/interface/HydrawiseSettingsInterface";
import SensorDataInterface from "../../store/interface/SensorDataInterface";
import SensorPropertiesInterface from "../../store/interface/SensorPropertiesInterface";
import WaterFilterDataInterface from "../../store/interface/WaterFilterDataInterface";
import WaterFilterPropertiesInterface from "../../store/interface/WaterFilterPropertiesInterface";
import FertilizerDataInterface from "../../store/interface/FertilizerDataInterface";
import FertilizerPropertiesInterface from "../../store/interface/FertilizerPropertiesInterface";
import { get, postJSON, urlDecorator } from "../../utils/http.utils";
import { HydrawiseSettingsResponseInterface } from "./types";

const CORS_CONFIG = {
  credentials: "include",
  mode: "cors",
};

function withAccessCheck(e, accessErrorCallback) {
  // @ts-ignore
  if (e.message === "Access Denied") {
    accessErrorCallback?.();
  }
}

class CalcApi {
  async generateImage(
    plan_id: string | undefined,
    plan: any,
    layouts: any,
    textsVisibility: any,
    additionalParams: any,
    accessErrorCallback: Function | undefined
  ) {
    try {
      console.debug("Generate image");
      if (plan == null) {
        throw new Error("Invalid parameter value");
      }

      const res = await postJSON(
        urlDecorator(`$CALC_BACKEND_URL/${plan_id}/generatePNG`),
        {
          ident: plan_id,
          plan,
          layouts,
          textsVisibility,
          additionalParams,
        },
        CORS_CONFIG
      );

      const { status, error_msg, response } = await res.json();

      if (status === "ok") return response;

      if (error_msg) throw new Error(error_msg);
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      throw e;
    }
  }

  async recalculateCachesByPlan(
    plan_id: string,
    accessErrorCallback: Function | undefined
  ) {
    try {
      console.debug("recalculate caches");
      if (plan_id == null) {
        throw new Error("Invalid parameter value");
      }

      const res = await postJSON(
        urlDecorator(`$CALC_BACKEND_URL/${plan_id}/recalculatePlanCaches`),
        {
          ident: plan_id,
        },
        CORS_CONFIG
      );

      const { error_msg } = await res.json();

      if (error_msg) throw new Error(error_msg);
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      throw e;
    }
  }

  async generateSensorCoverage(
    plan_id: string | undefined,
    plan: any,
    accessErrorCallback: Function | undefined
  ) {
    try {
      console.debug("Generate sensorCoverage");
      if (plan == null) {
        throw new Error("Invalid parameter value");
      }

      const res = await postJSON(
        urlDecorator(`$CALC_BACKEND_URL/${plan_id}/generateSensorCoverage`),
        {
          ident: plan_id,
          plan,
        },
        CORS_CONFIG
      );

      const { status, error_msg, response } = await res.json();

      if (status === "ok") return response;

      if (error_msg) throw new Error(error_msg);
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      throw e;
    }
  }

  async fetchSensorData(
    plan_id: string | undefined,
    serial_number: string[],
    accessErrorCallback: Function | undefined
  ): Promise<SensorDataInterface[] | undefined> {
    try {
      console.debug("Fetch sensors data");

      const serial_number_str = serial_number
        .map((s) => `serial_number[]=${s}`)
        .join("&");

      const response = await get(
        urlDecorator(
          `$SENSOR_BACKEND_URL/sensors/${plan_id}/sensorHistory?${serial_number_str}`
        ),
        {
          last: true,
        },
        CORS_CONFIG
      );

      const { result } = await response.json();
      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch sensor data", e);
    }

    return undefined;
  }

  _insertGapsByThresholdInMs(
    values: SensorDataInterface[] | undefined,
    threshold: number = 24 * 60 * 60 * 1000
  ): SensorDataInterface[] | undefined {
    if (values == null || values.length === 0) {
      return values;
    }

    let result = values.reduce(
      (acc: SensorDataInterface[], value: SensorDataInterface) => {
        const previous = acc.slice(-1)[0];
        if (previous && previous.date && value.date) {
          const previousTime = new Date(previous.date).getTime();
          const valueTime = new Date(value.date).getTime();
          if (Math.abs(valueTime - previousTime) > threshold) {
            acc.push({
              date: Math.min(previousTime, valueTime) + 1,
              serial_number: previous.serial_number,
            });
          }
        }
        acc.push(value);
        return acc;
      },
      []
    );
    return result;
  }

  async fetchSensorHistory(
    plan_id: string | undefined,
    serial_number: string,
    accessErrorCallback: Function | undefined
  ): Promise<SensorDataInterface[] | undefined> {
    console.debug("Fetch sensor history");

    try {
      const response = await get(
        urlDecorator(`$SENSOR_BACKEND_URL/sensors/${plan_id}/sensorHistory`),
        {
          serial_number,
        },
        CORS_CONFIG
      );

      const { result } = await response.json();
      if (result) {
        //TODO please FIXME when the START/STOP parameters are added to the query
        return this._insertGapsByThresholdInMs(result);
      }
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch sensor data history", e);
    }

    return undefined;
  }

  async fetchSensorProperties(
    plan_id: string,
    accessErrorCallback: Function | undefined
  ): Promise<any> {
    try {
      console.debug("Fetch sensors properties");

      const response = await get(
        urlDecorator(`$SENSOR_BACKEND_URL/sensors/${plan_id}/sensorProperties`),
        undefined,
        CORS_CONFIG
      );

      const { result, error } = await response.json();
      if (error) throw new Error(error);

      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch sensor properties", e);
    }

    return undefined;
  }

  async saveSensor(
    id: string,
    plan_id: string,
    properties: SensorPropertiesInterface,
    accessErrorCallback: Function | undefined
  ) {
    try {
      console.debug("Save sensor property");

      const response = await postJSON(
        urlDecorator(`$SENSOR_BACKEND_URL/sensors/${plan_id}/sensorProperties`),
        {
          ...properties,
          id,
          plan_id,
        },
        CORS_CONFIG
      );

      const { result, error } = await response.json();
      if (error) throw new Error(error);

      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch sensor properties", e);
    }

    return undefined;
  }

  async fetchWaterFilterProperties(
    plan_id: string,
    accessErrorCallback: Function | undefined
  ): Promise<WaterFilterPropertiesInterface | undefined> {
    try {
      const response = await get(
        urlDecorator(
          `$SENSOR_BACKEND_URL/waterfilter/${plan_id}/waterfilterProperties`
        ),
        undefined,
        CORS_CONFIG
      );

      const { result, error } = await response.json();
      if (error) throw new Error(error);

      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch water filter properties", e);
    }

    return undefined;
  }

  async saveWaterFilter(
    id: string,
    plan_id: string,
    properties: WaterFilterPropertiesInterface,
    accessErrorCallback: Function | undefined
  ) {
    try {
      const response = await postJSON(
        urlDecorator(
          `$SENSOR_BACKEND_URL/waterfilter/${plan_id}/waterfilterProperties`
        ),
        {
          ...properties,
          id,
          plan_id,
        },
        CORS_CONFIG
      );

      const { result, error } = await response.json();
      if (error) throw new Error(error);

      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch sensor properties", e);
    }

    return undefined;
  }

  async fetchWaterFilterData(
    plan_id: string | undefined,
    serial_number: string,
    accessErrorCallback: Function | undefined
  ): Promise<WaterFilterDataInterface[] | undefined> {
    try {
      const serial_number_str = `serial_number[]=${serial_number}`;

      const response = await get(
        urlDecorator(
          `$SENSOR_BACKEND_URL/waterfilter/${plan_id}/waterfilterHistory?${serial_number_str}`
        ),
        {
          last: true,
        },
        CORS_CONFIG
      );

      const { result } = await response.json();
      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch sensor data", e);
    }

    return undefined;
  }

  async fetchWaterFilterHistory(
    plan_id: string | undefined,
    serial_number: string,
    accessErrorCallback: Function | undefined
  ): Promise<WaterFilterDataInterface[] | undefined> {
    try {
      const serial_number_str = `serial_number[]=${serial_number}`;

      const response = await get(
        urlDecorator(
          `$SENSOR_BACKEND_URL/waterfilter/${plan_id}/waterfilterHistory?${serial_number_str}`
        ),
        undefined,
        CORS_CONFIG
      );

      const { result } = await response.json();
      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch sensor data", e);
    }

    return undefined;
  }

  async fetchNotificationProperties(
    plan_id: string,
    accessErrorCallback: Function | undefined
  ): Promise<any> {
    try {
      console.debug("Fetch notification properties");

      const response = await get(
        urlDecorator(`$SENSOR_BACKEND_URL/notification/${plan_id}/properties`),
        undefined,
        CORS_CONFIG
      );

      const { result, error } = await response.json();
      if (error) throw new Error(error);

      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch notification properties", e);
    }

    return undefined;
  }

  async saveNotificationProperties(
    plan_id: string,
    email: string | undefined,
    locale: string | undefined,
    active: boolean | undefined,
    accessErrorCallback: Function | undefined
  ) {
    try {
      console.debug("Save notification properties");

      const response = await postJSON(
        urlDecorator(`$SENSOR_BACKEND_URL/notification/${plan_id}/properties`),
        {
          plan_id,
          email,
          locale,
          active,
        },
        CORS_CONFIG
      );

      const { result, error } = await response.json();
      if (error) throw new Error(error);

      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in save notification properties", e);
    }

    return undefined;
  }

  async fetchHydrawiseSettings(
    plan_id: string,
    accessErrorCallback: Function | undefined
  ): Promise<HydrawiseSettingsResponseInterface> {
    try {
      console.debug("Fetch hydrawise settings");

      const response = await get(
        urlDecorator(
          `$SENSOR_BACKEND_URL/smartcontroller/${plan_id}/properties`
        ),
        undefined,
        CORS_CONFIG
      );

      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result) throw new Error("Hydrawise settings cannot be empty");

      return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch hydrawise settings", e);
      throw e;
    }
  }

  async saveHydrawiseSettings(
    plan_id: string,
    controller_type: ControllerType,
    circuitAssigned: CircuitAssigned,
    locationName: string,
    noIrrTimeStart: string | undefined,
    noIrrTimeStop: string | undefined,
    noIrrDays: number[],
    noIrrTemperatureLimitInC: number | undefined,
    noIrrTemperatureEnabled: boolean,
    noIrrWindSpeedLimitInKmh: number | undefined,
    noIrrWindSpeedEnabled: boolean,
    accessErrorCallback: Function | undefined
  ): Promise<HydrawiseSettingsResponseInterface | undefined> {
    try {
      console.debug("Save hydrawise settings");

      const assigned: {
        circuit_id: string;
        relay_num: number | null;
        sensor_id: string | null;
        default_sensor_ids: string[];
        valve_num: number | undefined;
        et_env_precipitation_in_lm2_per_h: number | undefined;
        living_env_soil_condition_coef: number | undefined;
        living_env_vegetation_coef: number | undefined;
        living_env_root_depth_in_cm: number | undefined;
        living_env_soil_type_coef: number | undefined;
        living_env_sun_exposure_coef: number | undefined;
        living_env_enabled: boolean | undefined;
        et_env_irrigation_type: string | undefined;
        et_env_manual_adj_coef: number | undefined;
        et_env_manual_irrigation_time_in_min: number | undefined;
        et_env_enabled: boolean | undefined;
      }[] = [];

      for (const circuit of circuitAssigned) {
        assigned.push({
          circuit_id: circuit.id,
          relay_num: circuit.relay_num ?? null,
          sensor_id: circuit.sensor_id ?? null,
          default_sensor_ids: circuit.default_sensor_ids ?? [],
          valve_num: circuit?.valve_num,
          et_env_precipitation_in_lm2_per_h:
            circuit?.et_env_precipitation_in_lm2_per_h,
          living_env_soil_condition_coef:
            circuit?.living_env_soil_condition_coef,
          living_env_vegetation_coef: circuit?.living_env_vegetation_coef,
          living_env_root_depth_in_cm: circuit?.living_env_root_depth_in_cm,
          living_env_soil_type_coef: circuit?.living_env_soil_type_coef,
          living_env_sun_exposure_coef: circuit?.living_env_sun_exposure_coef,
          living_env_enabled: circuit?.living_env_enabled,
          et_env_irrigation_type: circuit?.et_env_irrigation_type,
          et_env_manual_adj_coef: circuit?.et_env_manual_adj_coef,
          et_env_manual_irrigation_time_in_min:
            circuit?.et_env_manual_irrigation_time_in_min,
          et_env_enabled: circuit?.et_env_enabled,
        });
      }

      const response = await postJSON(
        urlDecorator(
          `$SENSOR_BACKEND_URL/smartcontroller/${plan_id}/properties`
        ),
        {
          controller_type,
          assigned,
          location_name: locationName,
          no_irr_time_start: noIrrTimeStart,
          no_irr_time_stop: noIrrTimeStop,
          no_irr_days: noIrrDays,
          no_irr_temperature_limit_in_c: noIrrTemperatureLimitInC,
          no_irr_wind_speed_limit_in_kmh: noIrrWindSpeedLimitInKmh,
          no_irr_temperature_enabled: noIrrTemperatureEnabled,
          no_irr_wind_speed_enabled: noIrrWindSpeedEnabled,
        },
        CORS_CONFIG
      );

      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (result == null) throw new Error("Hydrawise settings cannot be empty");

      return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in save hydrawise settings", e);
      throw e;
    }
  }

  async loginHunterApi(
    plan_id: string,
    hydrawiseApiKey: string,
    accessErrorCallback: Function | undefined
  ) {
    try {
      console.debug("Login to hunterAPI");

      const response = await postJSON(
        urlDecorator(`$SENSOR_BACKEND_URL/smartcontroller/${plan_id}/login`),
        {
          hydrawiseApiKey: hydrawiseApiKey || null,
        },
        CORS_CONFIG
      );

      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in login to hunterAPI", e);
      throw e;
    }
  }

  async changeHydrawiseController(
    plan_id: string,
    controllerId: string,
    accessErrorCallback: Function | undefined
  ) {
    try {
      console.debug("Change hydrawise controller");

      const response = await postJSON(
        urlDecorator(
          `$SENSOR_BACKEND_URL/smartcontroller/${plan_id}/changeController`
        ),
        {
          controllerId: controllerId || null,
        },
        CORS_CONFIG
      );

      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in change hydrawise controller", e);
      throw e;
    }
  }

  async fetchFertilizerProperties(
    plan_id: string,
    accessErrorCallback: Function | undefined
  ): Promise<FertilizerPropertiesInterface | undefined> {
    try {
      const response = await get(
        urlDecorator(
          `$SENSOR_BACKEND_URL/fertilizer/${plan_id}/fertilizerProperties`
        ),
        undefined,
        CORS_CONFIG
      );

      const { result, error } = await response.json();
      if (error) throw new Error(error);

      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch fertilizer properties", e);
    }

    return undefined;
  }

  async saveFertilizer(
    id: string,
    plan_id: string,
    properties: FertilizerPropertiesInterface,
    accessErrorCallback: Function | undefined
  ) {
    try {
      const response = await postJSON(
        urlDecorator(
          `$SENSOR_BACKEND_URL/fertilizer/${plan_id}/fertilizerProperties`
        ),
        {
          ...properties,
          id,
          plan_id,
        },
        CORS_CONFIG
      );

      const { result, error } = await response.json();
      if (error) throw new Error(error);

      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Save fertilizer properties failed", e);
    }

    return undefined;
  }

  async fetchFertilizerData(
    plan_id: string | undefined,
    serialNumber: string,
    accessErrorCallback: Function | undefined
  ): Promise<FertilizerDataInterface[] | undefined> {
    try {
      const serial_number_str = `serial_number[]=${serialNumber}`;

      const response = await get(
        urlDecorator(
          `$SENSOR_BACKEND_URL/fertilizer/${plan_id}/fertilizerHistory?${serial_number_str}`
        ),
        {
          last: true,
        },
        CORS_CONFIG
      );

      const { result } = await response.json();
      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);
      console.error("Error in fetch fertilizer last data", e);
    }

    return undefined;
  }

  async fetchFertilizerHistory(
    plan_id: string | undefined,
    serial_number: string,
    accessErrorCallback: Function | undefined
  ): Promise<FertilizerDataInterface[] | undefined> {
    try {
      const serial_number_str = `serial_number[]=${serial_number}`;

      const response = await get(
        urlDecorator(
          `$SENSOR_BACKEND_URL/fertilizer/${plan_id}/fertilizerHistory?${serial_number_str}`
        ),
        undefined,
        CORS_CONFIG
      );

      const { result } = await response.json();
      if (result) return result;
    } catch (e) {
      withAccessCheck(e, accessErrorCallback);

      console.error("Error in fetch fertilizer history data", e);
    }

    return undefined;
  }
}

const calcApi = new CalcApi();
export default calcApi;
